import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getFriendlyErrorInfo, isAxiosErrorLike } from "utils/httpError";

const AppErrorContext = createContext(null);

export const AppErrorProvider = ({ children }) => {
  const [currentError, setCurrentError] = useState(null);

  const clearError = useCallback(() => {
    setCurrentError(null);
  }, []);

  const notifyError = useCallback((error, options = {}) => {
    const info = getFriendlyErrorInfo(error, options);
    setCurrentError({
      open: true,
      title: info.title,
      message: info.message,
      details: info.details,
      status: info.status,
      severity: options.severity || "error",
    });
  }, []);

  useEffect(() => {
    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      if (!isAxiosErrorLike(reason)) return;

      notifyError(reason);

      // Prevent the default browser/React overlay for Axios request failures.
      // We only do this for Axios-like rejections to avoid hiding real code bugs.
      if (typeof event?.preventDefault === "function") {
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [notifyError]);

  const value = useMemo(
    () => ({
      currentError,
      notifyError,
      clearError,
    }),
    [currentError, notifyError, clearError],
  );

  return (
    <AppErrorContext.Provider value={value}>
      {children}
    </AppErrorContext.Provider>
  );
};

export const useAppError = () => {
  const ctx = useContext(AppErrorContext);
  if (!ctx) {
    throw new Error("useAppError must be used within an AppErrorProvider");
  }
  return ctx;
};
